import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, User, CreditCard, Users, ArrowLeftRight, History, AlertCircle, Plus, Minus } from 'lucide-react';
import {
  adjustMembershipCredits,
  fetchUserMembershipDetail,
  manualCancelMembership,
  manualExpireMembership,
} from '../../../services/MasterAdmin/membershipService';
import {
  connectMembershipAdminSocket,
  registerMembershipAdminSocket,
} from '../../../services/MasterAdmin/membershipAdminSocket';
import { StatusBadge } from './MembershipAlerts';
import { msInput, msPrimaryBtn, msSecondaryBtn, formatMembershipDateTime } from './membershipUi';
import ConfirmDialog from '../ui/ConfirmDialog';

interface Props {
  membershipId: string;
  onBack: () => void;
}

function SectionCard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-4 dark:border-gray-700">
        <span className="text-emerald-600 dark:text-emerald-400">{icon}</span>
        <h3 className="text-sm font-bold text-gray-900 dark:text-white">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-gray-50 last:border-0 dark:border-gray-800">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</span>
      <span className="text-right text-sm font-medium text-gray-900 dark:text-white">{value || '—'}</span>
    </div>
  );
}

const ledgerTypeLabel: Record<string, string> = {
  GRANT: 'Plan grant',
  USE: 'Used in consultation',
  ADJUST: 'Manual adjustment by admin',
  CARRYFORWARD: 'Carry-forward from previous month',
  EXPIRE: 'Expired unused',
};

const MembershipMemberDetailPage: React.FC<Props> = ({ membershipId, onBack }) => {
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creditAmount, setCreditAmount] = useState('1');
  const [creditNotes, setCreditNotes] = useState('');
  const [creditBusy, setCreditBusy] = useState(false);
  const [creditError, setCreditError] = useState('');
  const [creditSuccess, setCreditSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<{ title: string; message: string; onConfirm: () => Promise<void> } | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'credits' | 'family' | 'transfers' | 'payments'>('overview');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const d = await fetchUserMembershipDetail(membershipId);
      setDetail(d);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load details');
    } finally {
      setLoading(false);
    }
  }, [membershipId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    const socket = connectMembershipAdminSocket(token);
    const onRefresh = (payload: { userMembershipId?: string }) => {
      if (!payload?.userMembershipId || payload.userMembershipId === membershipId) {
        void load();
      }
    };
    socket.on('membershipAdminRefresh', onRefresh);
    void registerMembershipAdminSocket(socket, token);
    return () => {
      socket.off('membershipAdminRefresh', onRefresh);
      socket.disconnect();
    };
  }, [membershipId, load]);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-10">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 p-10">
        <AlertCircle className="h-10 w-10 text-red-400" />
        <p className="text-sm text-gray-500">{error || 'Member not found'}</p>
        <button type="button" className={msSecondaryBtn} onClick={onBack}>← Back to list</button>
      </div>
    );
  }

  const m = detail.membership || {};
  const plan = m.plan || {};
  const user = m.user || {};
  const credits = detail.credits || {};
  const family: any[] = detail.family || [];
  const ledger: any[] = detail.ledger || [];
  const payments: any[] = detail.payments || [];
  const transfers: any[] = detail.transfers || [];

  const daysLeft = m.endDate
    ? Math.max(0, Math.ceil((new Date(m.endDate).getTime() - Date.now()) / 86400000))
    : null;
  const isExpiring = daysLeft != null && daysLeft <= 14 && daysLeft > 0;
  const isActive = m.status === 'active' || m.status === 'paid';

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <User className="h-3.5 w-3.5" /> },
    { id: 'credits', label: `Credits (${ledger.length})`, icon: <CreditCard className="h-3.5 w-3.5" /> },
    { id: 'family', label: `Family (${family.length})`, icon: <Users className="h-3.5 w-3.5" /> },
    { id: 'transfers', label: `Transfers (${transfers.length})`, icon: <ArrowLeftRight className="h-3.5 w-3.5" /> },
    { id: 'payments', label: `Payments (${payments.length})`, icon: <History className="h-3.5 w-3.5" /> },
  ] as const;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {/* ── Top bar ── */}
      <div className="flex shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900 sm:px-6">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Members
        </button>
        <span className="text-gray-300 dark:text-gray-600">/</span>
        <span className="text-sm font-semibold text-gray-900 dark:text-white">
          {user.name || user.phone_number || m.userId}
        </span>
        <div className="ml-auto">
          <StatusBadge status={m.status} />
        </div>
      </div>

      {/* ── Profile hero ── */}
      <div className="shrink-0 bg-gradient-to-r from-emerald-600 to-teal-500 px-6 py-6">
        <div className="mx-auto flex max-w-5xl items-center gap-5">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-white/20 text-2xl font-bold text-white shadow">
            {(user.name || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-white">{user.name || 'Unknown User'}</h1>
            <p className="text-sm text-emerald-100">{user.phone_number || m.userId}</p>
            <p className="mt-0.5 text-sm font-semibold text-white">{m.planName}</p>
          </div>
          <div className="flex flex-shrink-0 flex-col items-end gap-1">
            {daysLeft != null && (
              <div className={`rounded-xl px-4 py-2 text-center ${isExpiring ? 'bg-orange-400/90' : 'bg-white/20'}`}>
                <div className="text-xl font-black text-white leading-none">{daysLeft}</div>
                <div className="text-xs text-white/80">days left</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Quick stats ── */}
      <div className="shrink-0 grid grid-cols-2 gap-3 border-b border-gray-100 bg-gray-50 px-6 py-3 dark:border-gray-700 dark:bg-gray-900/50 sm:grid-cols-4">
        {[
          { icon: '💊', label: 'Credits remaining', value: credits.unlimited ? 'Unlimited' : String(credits.remaining ?? 0) },
          { icon: '✓', label: 'Credits used', value: credits.unlimited ? '—' : String(credits.used ?? 0) },
          { icon: '👨‍👩‍👧', label: 'Family members', value: String(family.length) },
          { icon: '💳', label: 'Total paid', value: payments.length ? `₹${payments.filter((p) => p.status === 'paid').reduce((s: number, p: any) => s + Number(p.amount || 0), 0).toLocaleString('en-IN')}` : '₹0' },
        ].map((s) => (
          <div key={s.label} className="flex items-center gap-3 rounded-xl bg-white px-3 py-2.5 shadow-sm dark:bg-gray-800">
            <span className="text-xl">{s.icon}</span>
            <div>
              <div className="text-base font-bold text-gray-900 dark:text-white">{s.value}</div>
              <div className="text-xs text-gray-400">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div className="shrink-0 flex gap-0 overflow-x-auto border-b border-gray-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-900">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 border-b-2 px-4 py-3 text-xs font-semibold whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto max-w-5xl space-y-5">

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <>
              <div className="grid gap-5 md:grid-cols-2">
                <SectionCard title="User details" icon={<User className="h-4 w-4" />}>
                  <InfoRow label="Full name" value={user.name} />
                  <InfoRow label="Phone" value={user.phone_number} />
                  <InfoRow label="User ID" value={<span className="font-mono text-xs">{m.userId}</span>} />
                </SectionCard>

                <SectionCard title="Plan & membership" icon={<CreditCard className="h-4 w-4" />}>
                  <InfoRow label="Plan" value={m.planName || plan.name} />
                  <InfoRow label="Plan type" value={plan.type || '—'} />
                  <InfoRow label="Duration" value={plan.title || plan.duration || '—'} />
                  <InfoRow
                    label="Price"
                    value={
                      plan.pricing?.payable != null
                        ? `₹${Number(plan.pricing.payable).toLocaleString('en-IN')}`
                        : plan.price != null
                          ? `₹${Number(plan.price).toLocaleString('en-IN')}`
                          : '—'
                    }
                  />
                  <InfoRow label="Consultation credits" value={plan.consultationCreditsTotal ?? plan.consultationCreditsPerMonth ?? '—'} />
                  <InfoRow
                    label="Carry-forward"
                    value={plan.consultationCreditCarryForward ? `Yes (max ${plan.maxCarryForwardCredits || 0})` : 'No'}
                  />
                  <InfoRow
                    label="Health record storage"
                    value={
                      plan.storageQuotaBytes
                        ? `${Math.round(Number(plan.storageQuotaBytes) / (1024 * 1024))} MB`
                        : '—'
                    }
                  />
                  <InfoRow label="Transfers allowed" value={plan.maxTransfersPerYear ?? 0} />
                  <InfoRow label="Ambulance priority" value={plan.ambulancePriority ? 'Yes · fare at actual' : 'No'} />
                  <InfoRow label="GP consult fee" value={plan.gpConsultFee != null ? `₹${plan.gpConsultFee}` : '—'} />
                  <InfoRow label="Status" value={<StatusBadge status={m.status} />} />
                  <InfoRow label="Source" value={m.source || 'Purchase'} />
                  <InfoRow label="Start date" value={formatMembershipDateTime(m.startDate)} />
                  <InfoRow label="End date" value={formatMembershipDateTime(m.endDate)} />
                  {m.cancelledAt && <InfoRow label="Cancelled on" value={formatMembershipDateTime(m.cancelledAt)} />}
                  {m.cancelReason && <InfoRow label="Cancel reason" value={m.cancelReason} />}
                  {(plan.allowedDoctors || []).length ? (
                    <div className="pt-3">
                      <div className="text-xs font-medium uppercase tracking-wide text-gray-400">Assigned doctors</div>
                      <ul className="mt-2 space-y-1.5">
                        {(plan.allowedDoctors || []).map((row: any) => (
                          <li key={`${row.vendorId}-${row.specialityId}`} className="rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-900 dark:bg-sky-900/20 dark:text-sky-100">
                            {row.doctorName} · {row.specialityName}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {plan.description ? (
                    <div className="pt-3">
                      <div className="text-xs font-medium uppercase tracking-wide text-gray-400">Description</div>
                      <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">{plan.description}</p>
                    </div>
                  ) : null}
                </SectionCard>
              </div>

              {/* Danger zone */}
              {isActive && (
                <div className="rounded-2xl border border-red-100 bg-red-50/50 p-5 dark:border-red-900/30 dark:bg-red-900/10">
                  <h3 className="mb-1 text-sm font-bold text-gray-900 dark:text-white">Admin actions</h3>
                  <p className="mb-4 text-xs text-gray-500">
                    These actions are <strong>immediate and irreversible</strong>. Use only when necessary.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        setConfirm({
                          title: 'Cancel membership',
                          message: `Cancel ${user.name || 'this user'}'s ${m.planName} membership immediately? Their benefits will stop right now.`,
                          onConfirm: async () => {
                            await manualCancelMembership(m.userMembershipId, 'Admin cancel');
                            await load();
                          },
                        })
                      }
                      className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 dark:border-red-800 dark:bg-transparent dark:text-red-400 dark:hover:bg-red-900/20"
                    >
                      Cancel membership
                      <span className="ml-2 text-xs font-normal text-red-400">Stops benefits now</span>
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setConfirm({
                          title: 'Force expire',
                          message: `Force-expire ${user.name || 'this user'}'s membership as if the end date was reached today?`,
                          onConfirm: async () => {
                            await manualExpireMembership(m.userMembershipId, 'Admin expire');
                            await load();
                          },
                        })
                      }
                      className="rounded-lg border border-orange-200 bg-white px-4 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-50 dark:border-orange-800 dark:bg-transparent dark:text-orange-400 dark:hover:bg-orange-900/20"
                    >
                      Force expire
                      <span className="ml-2 text-xs font-normal text-orange-400">Treated as end-date reached</span>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* CREDITS TAB */}
          {activeTab === 'credits' && (
            <>
              {/* Add/remove credits */}
              <SectionCard title="Add or remove consultation credits" icon={<CreditCard className="h-4 w-4" />}>
                <p className="mb-4 text-xs text-gray-500">
                  Use a <strong>positive number</strong> (e.g. +1) to add a credit — for example when a doctor didn't show up and you want to compensate.
                  Use a <strong>negative number</strong> (e.g. -1) to deduct. Always enter a reason; it appears in the history below.
                </p>
                {creditError && (
                  <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                    {creditError}
                  </div>
                )}
                {creditSuccess && (
                  <div className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
                    {creditSuccess}
                  </div>
                )}
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">
                      Amount <span className="font-normal text-gray-400">(use − to deduct)</span>
                    </label>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                        onClick={() => setCreditAmount((v) => String(Number(v) - 1))}
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <input
                        type="number"
                        value={creditAmount}
                        onChange={(e) => setCreditAmount(e.target.value)}
                        className={`${msInput} w-20 text-center`}
                      />
                      <button
                        type="button"
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                        onClick={() => setCreditAmount((v) => String(Number(v) + 1))}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-300">Reason (required)</label>
                    <input
                      value={creditNotes}
                      onChange={(e) => setCreditNotes(e.target.value)}
                      placeholder="e.g. Doctor didn't show up — compensation"
                      className={`${msInput} w-full`}
                    />
                  </div>
                  <button
                    type="button"
                    className={msPrimaryBtn}
                    disabled={creditBusy || !creditNotes.trim()}
                    onClick={async () => {
                      setCreditError('');
                      setCreditSuccess('');
                      if (!creditNotes.trim()) { setCreditError('Please enter a reason.'); return; }
                      try {
                        setCreditBusy(true);
                        await adjustMembershipCredits(m.userMembershipId, { amount: Number(creditAmount), notes: creditNotes });
                        setCreditNotes('');
                        setCreditAmount('1');
                        setCreditSuccess(`Done — ${Number(creditAmount) > 0 ? '+' : ''}${creditAmount} credits applied.`);
                        await load();
                      } catch (e: any) {
                        setCreditError(e?.response?.data?.message || 'Failed to adjust credits');
                      } finally {
                        setCreditBusy(false);
                      }
                    }}
                  >
                    Apply
                  </button>
                </div>
              </SectionCard>

              {/* Ledger history */}
              <SectionCard title="Credit history" icon={<History className="h-4 w-4" />}>
                {ledger.length === 0 ? (
                  <p className="text-sm text-gray-400">No credit history yet.</p>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {ledger.map((row: any, i: number) => {
                      const isAdd = (row.amount ?? 0) > 0;
                      return (
                        <div key={row.ledgerId || i} className="flex items-center gap-3 py-3">
                          <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${isAdd ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400'}`}>
                            {isAdd ? '+' : '−'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">
                              {ledgerTypeLabel[row.type] || row.type}
                              <span className={`ml-2 font-bold ${isAdd ? 'text-emerald-600' : 'text-red-500'}`}>
                                {isAdd ? `+${row.amount}` : row.amount}
                              </span>
                            </div>
                            {row.notes && <div className="truncate text-xs text-gray-400">{row.notes}</div>}
                          </div>
                          {row.createdAt && (
                            <span className="flex-shrink-0 text-xs text-gray-400">
                              {new Date(row.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </SectionCard>
            </>
          )}

          {/* FAMILY TAB */}
          {activeTab === 'family' && (
            <SectionCard title="Family members on this plan" icon={<Users className="h-4 w-4" />}>
              {family.length === 0 ? (
                <p className="text-sm text-gray-400">No family members added to this plan.</p>
              ) : (
                <div className="space-y-3">
                  {family.map((f: any) => (
                    <div key={f.familyMemberId} className="flex items-center gap-4 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">
                        {(f.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 dark:text-white">{f.name}</div>
                        <div className="text-xs text-gray-400">
                          {f.relationship || 'Member'}
                          {f.phone ? ` · ${f.phone}` : ''}
                        </div>
                      </div>
                      {f.isPrimary && (
                        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                          Primary holder
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          )}

          {/* TRANSFERS TAB */}
          {activeTab === 'transfers' && (
            <SectionCard title="Membership transfer history" icon={<ArrowLeftRight className="h-4 w-4" />}>
              {transfers.length === 0 ? (
                <p className="text-sm text-gray-400">No transfers on this membership.</p>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {transfers.map((t: any, i: number) => (
                    <div key={t.transferId || i} className="flex items-start gap-3 py-4">
                      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300">
                        <ArrowLeftRight className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          From <span className="text-gray-500">{t.fromUserName || t.fromUserId || '—'}</span>
                          {' → '}
                          To <span className="text-gray-500">{t.toUserName || t.toUserId || '—'}</span>
                        </div>
                        {(t.fromUserPhone || t.toPhone) && (
                          <div className="text-xs text-gray-400">
                            {t.fromUserPhone && <span>From: {t.fromUserPhone}</span>}
                            {t.toPhone && <span className="ml-2">To: {t.toPhone}</span>}
                          </div>
                        )}
                        {t.reason && <div className="mt-1 text-xs text-gray-400">Reason: {t.reason}</div>}
                        {(t.benefits?.remainingLabel || t.benefitSnapshot) && (
                          <div className="mt-1 text-xs text-gray-500">
                            {(t.benefits?.remainingLabel) || `${t.benefitSnapshot?.remainingConsultations ?? 0} remaining consultations`}
                            {' · '}
                            Plan stays with the original member
                            {' · '}
                            {t.benefits?.storageLabel || '100 MB storage for recipient'}
                          </div>
                        )}
                        {t.credits && (
                          <div className="mt-1 text-xs text-emerald-700">
                            Recipient usage: {t.credits.unlimited ? 'Unlimited' : `${t.credits.used || 0} used · ${t.credits.remaining ?? 0} remaining`}
                          </div>
                        )}
                        {t.reviewNotes && <div className="mt-0.5 text-xs text-gray-400">Notes: {t.reviewNotes}</div>}
                      </div>
                      <div className="flex flex-shrink-0 flex-col items-end gap-1">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${
                          t.status === 'approved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' :
                          t.status === 'rejected' ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400' :
                          'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                        }`}>
                          {t.status}
                        </span>
                        {t.createdAt && (
                          <span className="text-xs text-gray-400">
                            {new Date(t.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          )}

          {/* PAYMENTS TAB */}
          {activeTab === 'payments' && (
            <SectionCard title="Purchase & payment history" icon={<History className="h-4 w-4" />}>
              {payments.length === 0 ? (
                <p className="text-sm text-gray-400">No payment records.</p>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {payments.map((p: any, i: number) => (
                    <div key={p.paymentId || i} className="flex items-center gap-4 py-4">
                      <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm ${
                        p.status === 'paid' ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700'
                      }`}>
                        💳
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-bold text-gray-900 dark:text-white">
                            ₹{Number(p.amount || 0).toLocaleString('en-IN')}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${
                            p.status === 'paid' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {p.status}
                          </span>
                        </div>
                        <div className="mt-0.5 font-mono text-xs text-gray-400 truncate">{p.paymentId}</div>
                        {p.gateway && <div className="text-xs text-gray-400">via {p.gateway}</div>}
                      </div>
                      {p.createdAt && (
                        <span className="flex-shrink-0 text-xs text-gray-400">
                          {new Date(p.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          )}

        </div>
      </div>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => {
          if (!confirm) return;
          try {
            setBusy(true);
            await confirm.onConfirm();
            setConfirm(null);
          } catch (e: any) {
            // error reflected in reloaded detail
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
};

export default MembershipMemberDetailPage;
